import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Utensils, Coffee, Clock, IndianRupee } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format price with currency
const formatPrice = (price, currency = "INR") => {
  if (!price) return null;
  
  if (currency === "INR") {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const HomestayCard = ({ homestay }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=800&h=450&fit=crop";
    if (imagePath.startsWith("http")) return imagePath;
    return `${BACKEND_URL}${imagePath}`;
  };

  const fallbackImage = "https://images.unsplash.com/photo-1664616466598-1f1a394f9009?w=800&h=450&fit=crop";
  const imageUrl = imageError ? fallbackImage : getImageUrl(homestay.images?.[0]);
  const price = formatPrice(homestay.price_per_night, homestay.price_currency);
  const perPersonPrice = formatPrice(homestay.price_per_person, homestay.price_currency);

  return (
    <Link
      to={homestay.slug ? `/homestay/${homestay.slug}` : `/listings/${homestay.id}`}
      className="group block listing-card rounded-2xl overflow-hidden bg-white shadow-soft"
      data-testid={`homestay-card-${homestay.id}`}
    >
      {/* Image - 16:9 aspect ratio with lazy loading */}
      <div className="relative aspect-video overflow-hidden bg-muted">
        {/* Skeleton loader */}
        {!imageLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-muted via-muted/80 to-muted" />
        )}
        <img
          src={imageUrl}
          alt={homestay.name}
          className={`w-full h-full object-cover listing-image transition-opacity duration-300 ${
            imageLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            setImageError(true);
            setImageLoaded(true);
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        
        {/* Featured Badge */}
        {homestay.is_featured && (
          <div className="absolute top-3 left-3">
            <Badge className="bg-accent text-white border-0 font-medium">Featured</Badge>
          </div>
        )}

        {/* Rating */}
        {homestay.average_rating > 0 && (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
            <Star className="w-4 h-4 fill-accent text-accent" />
            <span className="text-sm font-semibold">{homestay.average_rating.toFixed(1)}</span>
          </div>
        )}

        {/* Price Tag */}
        {(price || perPersonPrice) && (
          <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
            {price && (
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-primary">{price}</span>
                <span className="text-xs text-muted-foreground">/night</span>
              </div>
            )}
            {perPersonPrice && (
              <div className="flex items-center gap-1">
                <span className={`${price ? 'text-sm' : 'text-lg'} font-bold text-[#C86A3A]`}>{perPersonPrice}</span>
                <span className="text-xs text-muted-foreground">/person</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3
          className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-1"
         
        >
          {homestay.name}
        </h3>

        <div className="flex items-center gap-1 text-muted-foreground mb-3">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm line-clamp-1">{homestay.address}</span>
        </div>

        {/* Meal Info & Price (mobile fallback) */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {homestay.meal_included && (
              <div className="flex items-center gap-1 text-sm text-primary">
                <Utensils className="w-4 h-4" />
                <span>Meals</span>
              </div>
            )}
            {homestay.breakfast_included && (
              <div className="flex items-center gap-1 text-sm text-primary">
                <Coffee className="w-4 h-4" />
                <span>Breakfast</span>
              </div>
            )}
          </div>
          
          {/* Price shown inline on mobile if not featured on image */}
          {(price || perPersonPrice) && (
            <div className="md:hidden flex items-center gap-1 text-primary font-semibold">
              <span className="text-sm">{price || perPersonPrice}</span>
            </div>
          )}
        </div>

        {/* Check-in/Check-out Times */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="card-checkin-checkout">
          <Clock className="w-3.5 h-3.5" />
          <span>In: {homestay.check_in_time || "14:00"}</span>
          <span>•</span>
          <span>Out: {homestay.check_out_time || "11:00"}</span>
        </div>

        {/* Facilities */}
        {homestay.facilities?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {homestay.facilities.slice(0, 3).map((facility, index) => (
              <span
                key={index}
                className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded-full"
              >
                {facility}
              </span>
            ))}
            {homestay.facilities.length > 3 && (
              <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full">
                +{homestay.facilities.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default HomestayCard;
